/**
 * Typed Vera API client (openapi-fetch), generated-schema-driven.
 *
 * Rules this file encodes (see docs/architecture.md; historical rationale in
 * docs/archive/API_CONTRACT.md (git history) §4–5):
 *  - baseUrl = `${VITE_API_BASE_URL}/v1`. The spec's path keys are `/rounds`,
 *    `/me`, … (NO `/v1` prefix); `/v1` lives only in the server URL, which
 *    openapi-fetch ignores. Passing the env var raw would 404 every call.
 *  - Empty VITE_API_BASE_URL → DEMO mode (no real API; UI shows a DEMO badge
 *    and screens may fall back to mock data). DEMO is scoped strictly to "no
 *    API base URL configured".
 *  - Auth: a request-time middleware attaches `Authorization: Bearer <token>`
 *    ONLY when the auth layer has a token (cloud). Local dev sends no header.
 *  - Errors: every non-2xx is RFC-7807 application/problem+json; we THROW a
 *    typed `ApiError` carrying {code, status, detail}. Screens switch on the
 *    stable machine `code`, never on the human `detail`.
 *  - The ONE exception is `304 Not Modified`: it is not an error but the answer
 *    to a conditional GET the caller sent on purpose (`If-None-Match`, the
 *    round index probe, issue #334), so it passes through untouched and the
 *    caller reads `response.status`. `/v1` is `Cache-Control: no-store`, so no
 *    browser cache ever turns a 304 into a 200 behind our back.
 */

import createClient, { type Middleware } from "openapi-fetch";
import type { components, paths } from "./schema";

// ── Convenience aliases over the generated schema (import type only) ─────────
export type Schemas = components["schemas"];

export type MeResponse = Schemas["MeResponse"];
export type Role = Schemas["Role"];
export type RoundListResponse = Schemas["RoundListResponse"];
export type RoundListItem = Schemas["RoundListItem"];
export type CreateRoundRequest = Schemas["CreateRoundRequest"];
export type CreateRoundResponse = Schemas["CreateRoundResponse"];
export type PatchRoundRequest = Schemas["PatchRoundRequest"];
export type PatchRoundResponse = Schemas["PatchRoundResponse"];
export type CloseRoundResponse = Schemas["CloseRoundResponse"];
export type BatchReport = Schemas["BatchReport"];
export type BatchSummary = Schemas["BatchSummary"];
export type FinalDecisionCompact = Schemas["FinalDecisionCompact"];
export type PageFinalDecisionCompact = Schemas["PageFinalDecisionCompact"];
export type ExpedienteIndex = Schemas["ExpedienteIndex"];
export type ExpedienteIndexRow = Schemas["ExpedienteIndexRow"];
export type ExpedienteDetail = Schemas["ExpedienteDetail"];
export type EffectiveDisposition = Schemas["EffectiveDisposition"];
export type AdjudicationRequest = Schemas["AdjudicationRequest"];
export type AdjudicationResponse = Schemas["AdjudicationResponse"];
export type AdjudicationSummary = Schemas["AdjudicationSummary"];
export type DocumentsResponse = Schemas["DocumentsResponse"];
export type DocumentRef = Schemas["DocumentRef"];
export type DocumentPackageResponse = Schemas["DocumentPackageResponse"];
export type DocumentPackageLinkResponse = Schemas["DocumentPackageLinkResponse"];
export type DocumentPackagePreviewResponse = Schemas["DocumentPackagePreviewResponse"];
export type SignUploadRequest = Schemas["SignUploadRequest"];
export type SignUploadResponse = Schemas["SignUploadResponse"];
export type SignedUpload = Schemas["SignedUpload"];
export type ProcessResponse = Schemas["ProcessResponse"];
export type SingleExpedienteProcessRequest =
  Schemas["SingleExpedienteProcessRequest"];
export type SingleExpedienteProcessResponse =
  Schemas["SingleExpedienteProcessResponse"];
export type BatchStatus = Schemas["BatchStatus"];
export type RoundOverviewResponse = Schemas["RoundOverviewResponse"];
export type SystemError = Schemas["SystemError"];
export type Failure = Schemas["Failure"];
export type Flag = Schemas["Flag"];
export type Priority = Schemas["Priority"];
export type Decision = Schemas["Decision"];
export type AdjudicationDecision = Schemas["AdjudicationDecision"];
/** The served/effective disposition vocabulary: the engine `Decision` widened with
 * the human-only `AWAITING_RESUBMISSION` (decision 12). Matches the union that
 * `FinalDecisionCompact.decision` and `EffectiveDisposition.decision` carry. */
export type EffectiveDecision = Decision | AdjudicationDecision;
export type ProblemDetails = Schemas["ProblemDetails"];
export type ValidationProblemDetails = Schemas["ValidationProblemDetails"];

// ── Stable machine error codes (docs/architecture.md §API and console) ─────────────
// Screens switch on these; this union is for ergonomics, NOT a closed wire enum.
export type ApiErrorCode =
  | "AUTH_MISSING_TOKEN"
  | "AUTH_FORBIDDEN_ROLE"
  | "ROUND_NOT_FOUND"
  | "EXPEDIENTE_NOT_FOUND"
  | "AUDIT_NOT_FOUND"
  | "ADJUDICATION_VALIDATION"
  | "STORAGE_UNAVAILABLE"
  | "REPORT_UNAVAILABLE"
  | "OVERVIEW_UNAVAILABLE"
  | "INTEGRITY_VERIFICATION_FAILED"
  | "PROFILE_ID_VIOLATION"
  | "PROFILE_DOCUMENT_VIOLATION"
  | "ROUND_CONFLICT"
  | "ROUND_CLOSED"
  | "ROUND_HAS_PENDING_RESUBMISSIONS"
  | (string & {});

/** Per-field validation entry (422 bodies carry `errors[]`). */
export type ValidationFieldError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};

/**
 * Typed error thrown for every non-2xx response. Carries the RFC-7807 problem
 * body so callers switch on the stable machine `code`.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly detail?: string;
  readonly title?: string;
  readonly errors?: ValidationFieldError[];
  /**
   * Expediente ids still awaiting resubmission — present only on a
   * `ROUND_HAS_PENDING_RESUBMISSIONS` (409) from `POST /rounds/{id}/close`.
   */
  readonly pendingExpedientes?: string[];
  /**
   * How many reopens a `force=true` close would abandon — same 409 only.
   * Always equals `pending_expedientes.length` per the contract, but mapped
   * separately so the confirmation copy never depends on the list being sent.
   */
  readonly pendingCount?: number;

  constructor(
    problem: Partial<ValidationProblemDetails> & {
      status: number;
      pending_expedientes?: string[];
      pending_count?: number;
    },
  ) {
    super(problem.detail ?? problem.title ?? `HTTP ${problem.status}`);
    this.name = "ApiError";
    this.code = (problem.code ?? "UNKNOWN") as ApiErrorCode;
    this.status = problem.status;
    this.detail = problem.detail;
    this.title = problem.title;
    this.errors = problem.errors;
    this.pendingExpedientes = problem.pending_expedientes;
    this.pendingCount = problem.pending_count;
  }

  /** True when the failure is an authentication problem (401). */
  get isAuth(): boolean {
    return this.status === 401;
  }

  /** True when the failure is an authorization problem (403). */
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

// ── DEMO mode ────────────────────────────────────────────────────────────────
const RAW_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").trim();

/** DEMO mode is scoped strictly to "no API base URL configured". */
export const IS_DEMO = RAW_BASE === "";

/** Final base URL: host + `/v1`. Empty in DEMO mode (no real calls happen). */
const BASE_URL = IS_DEMO ? "" : `${RAW_BASE.replace(/\/+$/, "")}/v1`;

// ── Token provider wiring (set by the auth layer; avoids a circular import) ──
type TokenProvider = () => Promise<string | null> | string | null;
let tokenProvider: TokenProvider | null = null;

/**
 * Registered by the auth layer (AuthProvider). Returns a fresh Firebase ID
 * token in cloud mode, or null in local/dev mode (no header attached).
 */
export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

// ── Unauthorized hook (set by the auth layer; mirrors setTokenProvider) ──────
// F22 (2026-07-01): a mid-session token invalidation (revoked account, allowlist
// change) used to strand the SPA in a non-actionable 401 error loop — every screen
// kept rendering ProblemAlert with no path back to sign-in. The auth layer
// registers a handler that clears the session so RequireAuth redirects to /login.
let onUnauthorizedHandler: (() => void) | null = null;
let unauthorizedFiring = false;

export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorizedHandler = handler;
}

// ── Middleware: attach token at request time; throw ApiError on failure ──────
const authMiddleware: Middleware = {
  async onRequest({ request }) {
    if (tokenProvider) {
      const token = await tokenProvider();
      if (token) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
    }
    return request;
  },
  async onResponse({ response }) {
    if (response.ok || response.status === 304) {
      return response;
    }
    // Read the problem+json body (best-effort) and throw a typed error.
    let problem: Partial<ValidationProblemDetails> = {};
    try {
      const cloned = response.clone();
      const body = (await cloned.json()) as Partial<ValidationProblemDetails>;
      if (body && typeof body === "object") {
        problem = body;
      }
    } catch {
      // Non-JSON / empty body — fall through with just the status.
    }
    if (response.status === 401 && onUnauthorizedHandler && !unauthorizedFiring) {
      // Re-entrancy guard: clearing the session can itself trigger request
      // teardown; never loop. The ApiError below still throws for the caller.
      unauthorizedFiring = true;
      try {
        onUnauthorizedHandler();
      } finally {
        unauthorizedFiring = false;
      }
    }
    throw new ApiError({ ...problem, status: response.status });
  },
};

/**
 * The typed openapi-fetch client. Screens call `api.GET("/rounds/{round_id}/report", …)`,
 * `api.POST("/uploads/sign", …)`, etc. On success the `{ data }` is populated;
 * on failure the response middleware THROWS an `ApiError` (so use try/catch).
 */
export function createApiClient(baseUrl: string, fetchImpl?: typeof fetch) {
  const client = createClient<paths>({
    baseUrl,
    ...(fetchImpl ? { fetch: fetchImpl } : {}),
  });
  client.use(authMiddleware);
  return client;
}

export const api = createApiClient(BASE_URL);

export type ApiClient = typeof api;
